import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, Plus, Building2, Mail, Phone, MapPin, Calendar, DollarSign,
  TrendingUp, ShoppingCart, Package, ChevronRight, ChevronDown, RefreshCw,
  Edit2, Trash2, X, Save, Home, Award, Crown, Download, FileSpreadsheet, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '@/utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_TERMS = ['Cash', '7 Days', '14 Days', '30 Days', '60 Days', '90 Days'];

const CustomerManagement = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [topMonthly, setTopMonthly] = useState([]);
  const [topYearly, setTopYearly] = useState([]);
  const [outstandingOrders, setOutstandingOrders] = useState([]);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  
  // Create/Edit dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: 'South Africa',
    payment_terms: '30 Days',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  // Export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    customerFilter: '',
    dateFrom: '',
    dateTo: '',
    includeOverdue: 'yes',
    groupByCustomer: 'yes',
  });

  const handleExportOutstanding = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportOptions.customerFilter) params.append('customer_name', exportOptions.customerFilter);
      if (exportOptions.dateFrom) params.append('date_from', exportOptions.dateFrom);
      if (exportOptions.dateTo) params.append('date_to', exportOptions.dateTo);
      params.append('include_overdue', exportOptions.includeOverdue);
      params.append('group_by_customer', exportOptions.groupByCustomer);

      const response = await axios.get(`${BACKEND_URL}/api/customers/export/outstanding-orders?${params.toString()}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cust = exportOptions.customerFilter || 'All';
      link.setAttribute('download', `Outstanding_Orders_${cust}_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded successfully');
      setExportDialogOpen(false);
    } catch (error) {
      toast.error('Failed to export outstanding orders');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [customersRes, dashboardRes, topMonthRes, topYearRes, outstandingRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers`),
        axios.get(`${BACKEND_URL}/api/customers/dashboard/summary`),
        axios.get(`${BACKEND_URL}/api/customers/dashboard/top-customers?period=month`),
        axios.get(`${BACKEND_URL}/api/customers/dashboard/top-customers?period=year`),
        axios.get(`${BACKEND_URL}/api/customers/dashboard/outstanding-orders`)
      ]);
      
      setCustomers(customersRes.data);
      setDashboardData(dashboardRes.data);
      setTopMonthly(topMonthRes.data);
      setTopYearly(topYearRes.data);
      setOutstandingOrders(outstandingRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = () => {
    setEditingCustomer(null);
    setFormData({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: 'South Africa',
      payment_terms: '30 Days',
      notes: ''
    });
    setCreateDialogOpen(true);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      contact_person: customer.contact_person || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      country: customer.country || 'South Africa',
      payment_terms: customer.payment_terms || '30 Days',
      notes: customer.notes || ''
    });
    setCreateDialogOpen(true);
  };

  const handleSaveCustomer = async () => {
    if (!formData.name.trim()) {
      toast.error('Customer name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingCustomer) {
        await axios.put(`${BACKEND_URL}/api/customers/${editingCustomer.customer_id}`, formData);
        toast.success('Customer updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/customers`, formData);
        toast.success('Customer created');
      }
      setCreateDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomer = async (customer) => {
    if (!confirm(`Delete customer "${customer.name}"?`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/customers/${customer.customer_id}`);
      toast.success('Customer deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Award className="w-5 h-5 text-slate-300" />;
    if (rank === 3) return <Award className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 text-center text-slate-500 font-bold">{rank}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden" data-testid="customer-management">
      {/* Honeycomb Background */}
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute inset-0 w-full h-full opacity-30" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-customers" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse" patternTransform="scale(1.5)">
              <path 
                d="M28 0 L56 16.67 L56 50 L28 66.67 L0 50 L0 16.67 Z M28 100 L56 83.33 L56 116.67 L28 133.33 L0 116.67 L0 83.33 Z" 
                fill="none" 
                stroke="rgba(16, 185, 129, 0.5)" 
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-customers)" />
        </svg>
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/95 backdrop-blur-sm border-b border-emerald-500/20">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="text-slate-400 hover:text-white"
              data-testid="back-home-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
            <div className="h-6 w-px bg-slate-700" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide">Customer Management</h1>
                <p className="text-xs text-slate-500">Sales & Customer Analytics</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Tab Buttons */}
            <div className="flex bg-slate-800/50 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded transition-colors ${
                  activeTab === 'dashboard' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white'
                }`}
                data-testid="tab-dashboard"
              >
                Dashboard
              </button>
              <button
                onClick={() => setActiveTab('customers')}
                className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded transition-colors ${
                  activeTab === 'customers' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white'
                }`}
                data-testid="tab-customers"
              >
                Customers
              </button>
              <button
                onClick={() => setActiveTab('outstanding')}
                className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded transition-colors ${
                  activeTab === 'outstanding' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white'
                }`}
                data-testid="tab-outstanding"
              >
                Outstanding
              </button>
            </div>
            <Button 
              onClick={handleCreateCustomer} 
              className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/50"
              data-testid="add-customer-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Customer
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[1600px] mx-auto space-y-6">

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && dashboardData && (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <div className="bg-slate-900/60 border border-emerald-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-emerald-400" />
                    <span className="text-xs text-slate-400 uppercase">Customers</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{dashboardData.total_customers}</p>
                  <p className="text-xs text-slate-500">{dashboardData.active_customers} active</p>
                </div>

                <div className="bg-slate-900/60 border border-cyan-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-cyan-400" />
                    <span className="text-xs text-slate-400 uppercase">Total Value</span>
                  </div>
                  <p className="text-2xl font-bold text-cyan-400">{formatCurrency(dashboardData.total_order_value)}</p>
                </div>

                <div className="bg-slate-900/60 border border-green-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    <span className="text-xs text-slate-400 uppercase">Completed</span>
                  </div>
                  <p className="text-2xl font-bold text-green-400">{formatCurrency(dashboardData.total_completed_value)}</p>
                </div>

                <div className="bg-slate-900/60 border border-orange-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-orange-400" />
                    <span className="text-xs text-slate-400 uppercase">Outstanding</span>
                  </div>
                  <p className="text-2xl font-bold text-orange-400">{formatCurrency(dashboardData.total_outstanding_value)}</p>
                </div>

                <div className="bg-slate-900/60 border border-purple-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-purple-400" />
                    <span className="text-xs text-slate-400 uppercase">This Month</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-400">{formatCurrency(dashboardData.monthly_sales)}</p>
                </div>

                <div className="bg-slate-900/60 border border-blue-500/20 rounded-lg p-4 col-span-2">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-blue-400" />
                    <span className="text-xs text-slate-400 uppercase">Year to Date</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-400">{formatCurrency(dashboardData.yearly_sales)}</p>
                </div>
              </div>

              {/* Top Customers */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top 10 This Month */}
                <div className="bg-slate-900/60 border border-emerald-500/20 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-white uppercase mb-4 flex items-center gap-2">
                    <Crown className="w-5 h-5 text-yellow-400" />
                    Top 10 Customers - This Month
                  </h3>
                  {topMonthly.length > 0 ? (
                    <div className="space-y-2">
                      {topMonthly.map((customer) => (
                        <div key={customer.rank} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800">
                          <div className="flex items-center gap-3">
                            {getRankIcon(customer.rank)}
                            <div>
                              <p className="text-white font-medium">{customer.customer_name}</p>
                              <p className="text-xs text-slate-500">{customer.order_count} orders</p>
                            </div>
                          </div>
                          <p className="text-emerald-400 font-bold">{formatCurrency(customer.total_value)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No completed orders this month</p>
                  )}
                </div>

                {/* Top 10 This Year */}
                <div className="bg-slate-900/60 border border-blue-500/20 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-white uppercase mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-blue-400" />
                    Top 10 Customers - This Year
                  </h3>
                  {topYearly.length > 0 ? (
                    <div className="space-y-2">
                      {topYearly.map((customer) => (
                        <div key={customer.rank} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800">
                          <div className="flex items-center gap-3">
                            {getRankIcon(customer.rank)}
                            <div>
                              <p className="text-white font-medium">{customer.customer_name}</p>
                              <p className="text-xs text-slate-500">{customer.order_count} orders</p>
                            </div>
                          </div>
                          <p className="text-blue-400 font-bold">{formatCurrency(customer.total_value)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No completed orders this year</p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Customers Tab */}
          {activeTab === 'customers' && (
            <div className="bg-slate-900/60 border border-slate-700 rounded-lg overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-lg font-bold text-white uppercase flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-emerald-400" />
                  Customer List ({customers.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700 bg-slate-800/50">
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Customer ID</th>
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Name</th>
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Contact</th>
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Email</th>
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Phone</th>
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">City</th>
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Terms</th>
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Status</th>
                      <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr key={customer.customer_id} className="border-b border-slate-800 hover:bg-slate-800/50">
                        <td className="py-3 px-4 font-mono text-emerald-400 text-sm">{customer.customer_id}</td>
                        <td className="py-3 px-4 text-white font-medium">{customer.name}</td>
                        <td className="py-3 px-4 text-slate-300">{customer.contact_person || '-'}</td>
                        <td className="py-3 px-4 text-slate-300 text-sm">{customer.email || '-'}</td>
                        <td className="py-3 px-4 text-slate-300 text-sm">{customer.phone || '-'}</td>
                        <td className="py-3 px-4 text-slate-300">{customer.city || '-'}</td>
                        <td className="py-3 px-4 text-slate-300 text-sm">{customer.payment_terms}</td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-1 rounded ${customer.is_active ? 'bg-green-950 text-green-400' : 'bg-red-950 text-red-400'}`}>
                            {customer.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={() => handleEditCustomer(customer)} className="text-cyan-400 hover:text-cyan-300">
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteCustomer(customer)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {customers.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  No customers found. Click "Add Customer" to create one.
                </div>
              )}
            </div>
          )}

          {/* Outstanding Orders Tab */}
          {activeTab === 'outstanding' && (
            <div className="space-y-4">
              <div className="bg-slate-900/60 border border-orange-500/20 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white uppercase flex items-center gap-2">
                    <Package className="w-5 h-5 text-orange-400" />
                    Outstanding Orders by Customer
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {outstandingOrders.length} customers with outstanding orders
                  </p>
                </div>
                <Button
                  onClick={() => setExportDialogOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid="export-outstanding-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Export to Excel
                </Button>
              </div>

              {outstandingOrders.length > 0 ? (
                <div className="space-y-3">
                  {outstandingOrders.map((customer) => (
                    <div key={customer.customer_name} className="bg-slate-900/60 border border-slate-700 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedCustomer(expandedCustomer === customer.customer_name ? null : customer.customer_name)}
                        className="w-full p-4 flex items-center justify-between hover:bg-slate-800/50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <Building2 className="w-5 h-5 text-emerald-400" />
                          <div className="text-left">
                            <p className="text-white font-bold">{customer.customer_name}</p>
                            <p className="text-xs text-slate-500">{customer.order_count} outstanding orders</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <p className="text-orange-400 font-bold text-lg">{formatCurrency(customer.total_value)}</p>
                          {expandedCustomer === customer.customer_name ? (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </button>

                      {expandedCustomer === customer.customer_name && (
                        <div className="border-t border-slate-700 p-4">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-700">
                                <th className="text-left py-2 text-xs text-slate-400 uppercase">Order #</th>
                                <th className="text-left py-2 text-xs text-slate-400 uppercase">Part #</th>
                                <th className="text-right py-2 text-xs text-slate-400 uppercase">Qty</th>
                                <th className="text-right py-2 text-xs text-slate-400 uppercase">Value</th>
                                <th className="text-left py-2 text-xs text-slate-400 uppercase">Current Process</th>
                                <th className="text-left py-2 text-xs text-slate-400 uppercase">Due Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {customer.orders.map((order) => {
                                const dueDate = order.due_date ? new Date(order.due_date) : null;
                                const isOverdue = dueDate && dueDate < new Date();
                                return (
                                  <tr key={order.order_number} className="border-b border-slate-800">
                                    <td className="py-2 font-mono text-cyan-400">{order.order_number}</td>
                                    <td className="py-2 text-white">{order.part_number}</td>
                                    <td className="py-2 text-right text-slate-300">{order.quantity}</td>
                                    <td className="py-2 text-right text-emerald-400">{formatCurrency(order.value)}</td>
                                    <td className="py-2">
                                      <span className="text-xs px-2 py-1 bg-blue-950 text-blue-400 rounded">
                                        {order.current_process}
                                      </span>
                                    </td>
                                    <td className={`py-2 ${isOverdue ? 'text-red-400' : 'text-slate-300'}`}>
                                      {order.due_date ? formatDate(order.due_date) : '-'}
                                      {isOverdue && <span className="ml-1 text-xs">(Overdue)</span>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-8 text-center">
                  <Package className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500">No outstanding orders</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Create/Edit Customer Dialog */}

      {/* Export Outstanding Orders Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="bg-slate-900 border-emerald-500/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
              Export Outstanding Orders
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-400 uppercase text-xs">Customer</Label>
              <Select value={exportOptions.customerFilter} onValueChange={(v) => setExportOptions({ ...exportOptions, customerFilter: v === '__all__' ? '' : v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="export-customer-select">
                  <SelectValue placeholder="All Customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Customers</SelectItem>
                  {outstandingOrders.map((c) => (
                    <SelectItem key={c.customer_name} value={c.customer_name}>{c.customer_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Delivery From</Label>
                <Input
                  type="date"
                  value={exportOptions.dateFrom}
                  onChange={(e) => setExportOptions({ ...exportOptions, dateFrom: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  data-testid="export-date-from"
                />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Delivery To</Label>
                <Input
                  type="date"
                  value={exportOptions.dateTo}
                  onChange={(e) => setExportOptions({ ...exportOptions, dateTo: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  data-testid="export-date-to"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Include Overdue</Label>
                <Select value={exportOptions.includeOverdue} onValueChange={(v) => setExportOptions({ ...exportOptions, includeOverdue: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="export-overdue-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes — Include Overdue</SelectItem>
                    <SelectItem value="no">No — Exclude Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Group by Customer</Label>
                <Select value={exportOptions.groupByCustomer} onValueChange={(v) => setExportOptions({ ...exportOptions, groupByCustomer: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="export-group-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes — Group by Customer</SelectItem>
                    <SelectItem value="no">No — Flat List</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 text-xs text-slate-400 space-y-1">
              <p className="font-bold text-slate-300">Export includes:</p>
              <p>23 columns: Customer, PO, WO, Part, Quantities, Values, Dates, Process Status, Payment Terms, Estimated Invoice &amp; Payment Dates</p>
              <p>Formatted with frozen headers, auto-filters, currency &amp; date formatting, and total outstanding value</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setExportDialogOpen(false)} className="border-slate-600">Cancel</Button>
              <Button onClick={handleExportOutstanding} disabled={exporting} className="bg-emerald-600 hover:bg-emerald-500" data-testid="export-download-btn">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {exporting ? 'Generating...' : 'Download Excel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-slate-900 border-emerald-500/30 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-400" />
              {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-400 uppercase text-xs">Customer Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-slate-800 border-slate-700"
                placeholder="Company Name"
                data-testid="customer-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Contact Person</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Payment Terms</Label>
                <Select value={formData.payment_terms} onValueChange={(v) => setFormData({ ...formData, payment_terms: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TERMS.map((term) => (
                      <SelectItem key={term} value={term}>{term}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="email@company.com"
                />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="+27 123 456 789"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-slate-800 border-slate-700"
                placeholder="123 Industrial Road"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="Johannesburg"
                />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Country</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="South Africa"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-slate-800 border-slate-700"
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button onClick={handleSaveCustomer} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500" data-testid="save-customer-btn">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingCustomer ? 'Update' : 'Create'} Customer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerManagement;
